import { deleteEditor, EditorClass, getEditor } from "luxedo-canvas"
import { MaskBase, Polygon } from "luxedo-canvas/asset"
import { AddMask, DrawPath, TransformMode } from "luxedo-canvas/lib"
import { Track, TrackGroup } from "luxedo-canvas/tracks"
import { ClickForegroundMode } from "./editor-modes/ClickForegroundMode"

export type Resolution = { w: number; h: number }

export type ShapeOption =
	| "triangle"
	| "square"
	| "pentagon"
	| "hexagon"
	| "heptagon"
	| "octagon"
	| "freedraw"

export class ImageEditor {
	editor: EditorClass

	imageSrc: string
	renderedTracks: Array<Track> = []

	constructor(canvasContainerId: string, resolution: Resolution, imageSrc: string) {
		const { w: width, h: height } = resolution

		deleteEditor()

		this.editor = getEditor()
		this.editor.initialize(
			canvasContainerId,
			{
				width,
				height,
				duration: 1,
			},
			{
				// @ts-ignore
				canvasColor: "transparent",
				// @ts-ignore
				backgroundColor: "transparent",
			}
		)

		this.imageSrc = imageSrc
		this.editor.canvas.setBackground(imageSrc)
	}

	showMask(mask: MaskBase) {
		mask.selectable = false
		mask.strokeWidth = 0
		mask.path.strokeWidth = 0

		const shape = new Polygon(
			this.editor,
			{
				points: mask.points,
			},
			{
				...mask,
				stroke: "#faae1b",
				strokeWidth: 5,
			}
		)

		this.renderedTracks.push(
			this.editor.tracks.addAsset(shape, {
				name: mask.name,
				id: getTrackIdFromMask(mask),
				index: -1,
			})
		)
	}

	hideMask(mask: MaskBase, deregister: boolean = true) {
		const maskedGroup = this.editor.tracks.getNode(getTrackIdFromMask(mask)) as TrackGroup<any>
		if (deregister) {
			for (const id of maskedGroup.maskData["ids"]) {
				const mask = this.editor.masks.get(id)
				this.editor.masks.deregister(mask)
			}
		}

		this.editor.tracks.delete(maskedGroup)
		const index = this.renderedTracks.findIndex((t) => t.id === maskedGroup.id)
		if (index > -1) this.renderedTracks.splice(index, 1)

		this.editor.canvas.requestRenderAll()
	}

	async createMask(selectedShape: ShapeOption, selectedSection: string) {
		// Create the path

		let mask: MaskBase
		if (selectedShape === "freedraw") {
			const path = await DrawPath.CreateAsync(this.editor, {})
			path.selectable = false
			// Create a new mask from the new path
			mask = new MaskBase(path, {
				name: "test",
				data: {
					source: "ImageModifier",
					section: selectedSection,
				},
			})
		} else {
			const NUM_SIDE_MAP = {
				triangle: 3,
				square: 4,
				pentagon: 5,
				hexagon: 6,
				heptagon: 7,
				octagon: 8,
			}
			const numSides = NUM_SIDE_MAP[selectedShape]
			mask = await AddMask.CreateAsync(this.editor, {
				top: 0,
				left: 0,
				width: 10,
				height: 10,
				name: "New mask",
				shape: "Polygon",
				numSides,
				angle: 0,
				addOnComplete: false,
			})

			this.editor.masks.deregister(mask)
		}

		mask.strokeWidth = 0
		mask.path.strokeWidth = 0

		return mask
	}

	async editMask(mask: MaskBase) {
		this.hideMask(mask, false)

		return new Promise<MaskBase>((res, rej) => {
			mask.lock()
			TransformMode.ActivateTransform(this.editor, mask)
			this.showMask(mask)
			this.editor.mode.on("cancelled", rej)
			this.editor.mode.on("completed", () => res(mask))
		})
	}

	async removeBackground(masks: Array<MaskBase>) {
		return await ClickForegroundMode.CreateAsync(this.editor, {
			masks,
			imageUrl: this.imageSrc,
			hideBeforeExport: this.renderedTracks,
		})
	}
}

function getTrackIdFromMask(mask: MaskBase) {
	return mask.id
}
